import React from 'react';
import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Input,
  Pagination,
  Row,
  Segmented,
  Spin,
  Typography,
  message,
} from 'antd';
import * as XLSX from 'xlsx';
import {
  CalendarOutlined,
  CheckCircleTwoTone,
  ClockCircleTwoTone,
  CopyOutlined,
  FileExcelOutlined,
  PhoneOutlined,
  SearchOutlined,
  SendOutlined,
  TeamOutlined,
  ThunderboltTwoTone,
} from '@ant-design/icons';
import { startCase } from 'lodash';
import DonutChartWidget from 'components/shared-components/DonutChartWidget';
import GoalWidget from 'components/shared-components/GoalWidget';
import { COLORS } from 'constants/ChartConstant';
import { PRIMARY } from '../CustomUtils.js/utils';

const { Text } = Typography;

const formatValue = (val) => {
  if (val === undefined || val === null) return '—';
  if (typeof val === 'object') {
    if (Array.isArray(val)) {
      return val.map((item) => formatValue(item)).join(', ');
    }
    return JSON.stringify(val);
  }

  const strVal = String(val).trim();
  if (!strVal) return '—';
  if (strVal.includes('@') || strVal.includes('://')) {
    return strVal;
  }
  if (strVal.includes(',')) {
    return strVal
      .split(',')
      .map((part) => startCase(part.trim()))
      .filter(Boolean)
      .join(', ');
  }
  return startCase(strVal);
};

const STATUS_META = {
  completed: { color: 'success', label: 'Completed' },
  in_progress: { color: 'warning', label: 'In progress' },
};

const STATUS_CHART_COLORS = {
  completed: '#52c41a',
  in_progress: '#faad14',
  no_response: '#d9d9d9',
};

const STATUS_COLOR_MAP = {
  completed: { bg: '#f0fff4', text: '#27ae60', border: '#b7ebc9' },
  in_progress: { bg: '#fff8ec', text: '#f39c12', border: '#fddfa0' },
};

const CARD_STYLE = { borderRadius: 18, boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)' };

export const copyLeadAnswers = async (answers) => {
  try {
    await navigator.clipboard.writeText(JSON.stringify(answers, null, 2));
    message.success('Copied');
  } catch {
    message.error('Copy failed');
  }
};

export const exportLeadsToExcel = (leads, fileName = 'leads.xlsx') => {
  if (!leads || leads.length === 0) {
    message.warning('No data to export');
    return;
  }

  const answerKeysSet = new Set();
  leads.forEach((lead) => {
    const answers = lead.answers || lead.response_json || {};
    Object.keys(answers).forEach((k) => answerKeysSet.add(k));
  });
  const answerKeys = Array.from(answerKeysSet);

  const data = leads.map((lead) => {
    const answers = lead.answers || lead.response_json || {};
    const row = {
      Name: lead.contact_name || '—',
      Number: lead.wa_id || '—',
    };

    answerKeys.forEach((key) => {
      row[startCase(key)] = answers[key] !== undefined && answers[key] !== null ? String(answers[key]) : '—';
    });

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
  XLSX.writeFile(workbook, fileName);
};

const shadeColor = (hex, percent) => {
  const normalized = hex.replace('#', '');
  const num = parseInt(
    normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized,
    16,
  );
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const adjust = (channel) => {
    const target = percent < 0 ? 0 : 255;
    const ratio = Math.abs(percent) / 100;
    return Math.round((target - channel) * ratio + channel);
  };
  r = adjust(r);
  g = adjust(g);
  b = adjust(b);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const FUNNEL_STOPS = [
  shadeColor(PRIMARY, 38),
  shadeColor(PRIMARY, 18),
  PRIMARY,
  shadeColor(PRIMARY, -18),
  shadeColor(PRIMARY, -36),
];

const StatCard = ({ icon, title, value, suffix, accent }) => (
  <Card
    size="small"
    bordered={false}
    style={{
      borderRadius: 18,
      background: '#ffffff',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)',
    }}
    bodyStyle={{ padding: '24px 20px' }}
  >
    <div
      style={{
        position: 'absolute',
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: accent,
        top: -20,
        right: -20,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, position: 'relative', zIndex: 1 }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        {icon}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: '#1a3353', lineHeight: 1.2 }}>
          {value !== undefined && value !== null ? value : '—'}
          {suffix && <span style={{ fontSize: 14, marginLeft: 2 }}>{suffix}</span>}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#7a8894' }}>
          {title}
        </div>
      </div>
    </div>
  </Card>
);

const FlowFunnel = ({ funnel }) => {
  const steps = (funnel || []).filter(Boolean);
  if (steps.length === 0) {
    return null;
  }

  const maxCount = Math.max(...steps.map((s) => s.count), 1);
  const MIN_W = 26;
  const widthPct = (count) => MIN_W + (Math.max(0, count) / maxCount) * (100 - MIN_W);

  const STEP_H = 74;
  const GAP_H = 8;
  const n = steps.length;
  const totalH = n * STEP_H + (n - 1) * GAP_H;

  const topW = widthPct(steps[0].count);
  const botW = widthPct(steps[n - 1].count);
  const topL = 50 - topW / 2;
  const topR = 50 + topW / 2;
  const botL = 50 - botW / 2;
  const botR = 50 + botW / 2;

  const outline = [
    `M ${topL} 0`,
    `L ${topR} 0`,
    `C ${topR} ${totalH * 0.38}, ${botR} ${totalH * 0.58}, ${botR} ${totalH}`,
    `L ${botL} ${totalH}`,
    `C ${botL} ${totalH * 0.58}, ${topL} ${totalH * 0.38}, ${topL} 0`,
    'Z',
  ].join(' ');

  const stepData = steps.map((step, idx) => {
    const totalPctVal = maxCount > 0 ? Math.round((step.count / maxCount) * 100) : 0;
    const prev = idx > 0 ? steps[idx - 1].count : null;
    const conversion = prev != null && prev > 0 ? Math.round((step.count / prev) * 100) : null;
    return { ...step, totalPct: totalPctVal, conversion };
  });

  return (
    <Card size="small" bordered={false} style={CARD_STYLE} bodyStyle={{ padding: '24px 20px' }}>
      <h4 className="font-weight-bold mb-3">Conversion funnel</h4>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ width: 60, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {stepData.map((step, idx) => (
            <div
              key={`step-label-${step.key || idx}`}
              style={{
                height: STEP_H,
                marginTop: idx > 0 ? GAP_H : 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Text type="secondary" style={{ fontSize: 11 }}>Step {idx + 1}</Text>
            </div>
          ))}
        </div>

        <div style={{ flex: 1, position: 'relative', height: totalH }}>
          <svg
            viewBox={`0 0 100 ${totalH}`}
            preserveAspectRatio="none"
            width="100%"
            height={totalH}
            style={{ display: 'block' }}
          >
            <defs>
              <linearGradient id="flow-leads-funnel-grad" x1="0" y1="0" x2="0" y2="1">
                {FUNNEL_STOPS.map((color, i) => (
                  <stop
                    key={i}
                    offset={`${(i / (FUNNEL_STOPS.length - 1)) * 100}%`}
                    stopColor={color}
                  />
                ))}
              </linearGradient>
            </defs>
            <path d={outline} fill="url(#flow-leads-funnel-grad)" />
            {Array.from({ length: n - 1 }, (_, i) => {
              const gapY = (i + 1) * STEP_H + i * GAP_H;
              return (
                <rect key={i} x="0" y={gapY} width="100" height={GAP_H} fill="white" />
              );
            })}
          </svg>

          {stepData.map((step, idx) => (
            <div
              key={`step-text-${step.key || idx}`}
              style={{
                position: 'absolute',
                top: idx * (STEP_H + GAP_H),
                height: STEP_H,
                left: 0,
                right: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                textAlign: 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.15)',
                pointerEvents: 'none',
              }}
            >
              <span style={{ fontSize: 12, opacity: 0.92, lineHeight: 1.1 }}>{step.label}</span>
              <span style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25 }}>{step.count}</span>
              <span style={{ fontSize: 11, opacity: 0.85 }}>({step.totalPct}%)</span>
            </div>
          ))}
        </div>

        <div style={{ width: 74, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          {stepData.map((step, idx) => (
            <div
              key={`step-conv-${step.key || idx}`}
              style={{
                height: STEP_H,
                marginTop: idx > 0 ? GAP_H : 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {step.conversion !== null && (
                <Text
                  style={{
                    fontSize: 11,
                    color: step.conversion >= 50 ? '#52c41a' : '#fa8c16',
                    fontWeight: 600,
                  }}
                >
                  {step.conversion}% kept
                </Text>
              )}
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export const FlowLeadsStatCards = ({ stats }) => {
  if (!stats) {
    return null;
  }

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={12} sm={8} lg={6}>
        <StatCard
          icon={<SendOutlined style={{ color: '#1677ff' }} />}
          title="Messages Sent"
          value={stats.flow_sent}
          accent="#eef5ff"
        />
      </Col>
      <Col xs={12} sm={8} lg={6}>
        <StatCard
          icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
          title="Completed"
          value={stats.completed}
          accent="#f0fbf1"
        />
      </Col>
      {stats.is_dynamic && (
        <Col xs={12} sm={8} lg={6}>
          <StatCard
            icon={<ClockCircleTwoTone twoToneColor="#faad14" />}
            title="In Progress"
            value={stats.in_progress}
            accent="#fffbef"
          />
        </Col>
      )}
      <Col xs={12} sm={8} lg={6}>
        <StatCard
          icon={<ThunderboltTwoTone twoToneColor="#8c8c8c" />}
          title="No Response"
          value={stats.no_response ?? Math.max(0, stats.flow_sent - stats.total_submissions)}
          accent="#f5f5f5"
        />
      </Col>
    </Row>
  );
};

export const FlowLeadsCharts = ({ stats, chartsReady }) => {
  const charts = stats?.charts;
  if (!charts || !stats?.flow_sent) {
    return null;
  }

  const breakdown = (charts.status_breakdown || []).filter((item) => item.count > 0);
  const funnel = charts.funnel || [];
  const totalForBreakdown = breakdown.reduce((sum, item) => sum + item.count, 0);
  const completedPct = stats.completion_rate ?? (
    stats.flow_sent > 0 ? Math.round((stats.completed / stats.flow_sent) * 100) : 0
  );
  const inProgressPct = stats.in_progress_rate ?? (
    stats.flow_sent > 0 ? Math.round((stats.in_progress / stats.flow_sent) * 100) : 0
  );
  const isDynamic = Boolean(stats.is_dynamic);

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
      <Col xs={24} lg={10}>
        {breakdown.length > 0 ? (
          chartsReady ? (
            <DonutChartWidget
              series={breakdown.map((item) => item.count)}
              labels={breakdown.map((item) => {
                const pct = totalForBreakdown > 0
                  ? Math.round((item.count / totalForBreakdown) * 100)
                  : 0;
                return `${item.label} (${pct}%)`;
              })}
              title="Total"
              height={220}
              customOptions={{
                colors: breakdown.map((item) => STATUS_CHART_COLORS[item.key] || COLORS[0]),
                legend: { position: 'bottom' },
              }}
              bodyClass="mb-0 mt-1"
              bordered={false}
              cardStyle={CARD_STYLE}
            />
          ) : (
            <Card size="small" bordered={false} style={{ ...CARD_STYLE, minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin />
            </Card>
          )
        ) : (
          <Card size="small" bordered={false} style={{ ...CARD_STYLE, minHeight: 260 }}>
            <Empty description="No status data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </Card>
        )}
      </Col>

      <Col xs={24} lg={14}>
        <FlowFunnel funnel={funnel} />
      </Col>
      <Col xs={isDynamic ? 12 : 24}>
        <GoalWidget
          title="Completed"
          value={completedPct}
          size={130}
          strokeWidth={8}
          subtitle={`${stats.completed} of ${stats.flow_sent} sent`}
          bordered={false}
          cardStyle={{ ...CARD_STYLE, marginBottom: 0 }}
        />
      </Col>
      {isDynamic && (
        <Col xs={12}>
          <GoalWidget
            title="In progress"
            value={inProgressPct}
            size={130}
            strokeWidth={8}
            subtitle={`${stats.in_progress} of ${stats.flow_sent} sent`}
            bordered={false}
            cardStyle={{ ...CARD_STYLE, marginBottom: 0 }}
          />
        </Col>
      )}
    </Row>
  );
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0].slice(0, 2).toUpperCase();
};

export const LeadCard = ({ lead, onCopy }) => {
  const answers = lead.answers || lead.response_json || {};
  const statusMeta = STATUS_META[lead.status] || { color: 'default', label: (lead.status || 'unknown').replace('_', ' ') };
  const statusStyle = STATUS_COLOR_MAP[lead.status] || { bg: '#f5f5f5', text: '#8c8c8c', border: '#d9d9d9' };
  const entries = Object.entries(answers);
  const displayName = lead.contact_name || lead.wa_id || '—';
  const formattedDate = lead.updated_at
    ? new Date(lead.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' })
    : '—';

  return (
    <Card
      size="small"
      hoverable
      bordered={false}
      style={{
        borderRadius: 18,
        height: '100%',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        borderTop: '3px solid #25D366',
      }}
      bodyStyle={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <Avatar
            shape="square"
            size={38}
            style={{ background: '#e8f5e9', color: '#27ae60', fontWeight: 700, fontSize: 13, flexShrink: 0, borderRadius: 10 }}
          >
            {getInitials(lead.contact_name || lead.wa_id)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 14, display: 'block', lineHeight: 1.3 }} ellipsis>
              {displayName}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <CalendarOutlined style={{ fontSize: 11 }} />
              <Text style={{ fontSize: 11 }}>{formattedDate}</Text>
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: statusStyle.bg,
            border: `1px solid ${statusStyle.border}`,
            borderRadius: 20,
            padding: '3px 10px',
            flexShrink: 0,
          }}
        >
          <CheckCircleTwoTone twoToneColor={statusStyle.text} style={{ fontSize: 12 }} />
          <Text style={{ fontSize: 11, color: statusStyle.text, fontWeight: 600 }}>{statusMeta.label}</Text>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <PhoneOutlined style={{ color: '#25D366', fontSize: 14 }} />
        <Text style={{ fontSize: 13, fontWeight: 500, color: '#1a3353' }}>{lead.wa_id || '—'}</Text>
      </div>

      {entries.length === 0 ? (
        <div style={{ padding: '10px 0', textAlign: 'center' }}>
          <Text style={{ fontSize: 12 }}>No answers submitted yet</Text>
        </div>
      ) : (
        <div style={{ flex: 1 }}>
          {entries.map(([key, value]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '7px 0',
              }}
            >
              <div style={{ width: 100, flexShrink: 0 }}>
                <Text style={{ fontSize: 12 }} ellipsis>
                  {startCase(key)}
                </Text>
              </div>
              <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
                <Text style={{ fontSize: 13, fontWeight: 500, color: '#1a3353', display: 'block', wordBreak: 'break-word' }}>
                  {formatValue(value)}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}

      <Divider style={{ margin: '12px 0 8px' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ fontSize: 12 }}>↗</span>
          {lead.meta_flow_name || 'Flow'}
        </Text>
        <Button
          type="text"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => onCopy(answers)}
          style={{ color: '#595959', fontSize: 12, paddingRight: 0 }}
        >
          Copy
        </Button>
      </div>
    </Card>
  );
};

export const FlowLeadsAnalyticsTab = ({ stats, chartsReady, loading }) => {
  if (!stats) {
    return !loading ? <Empty description="No stats available" /> : null;
  }

  return (
    <>
      <FlowLeadsStatCards stats={stats} />
      <FlowLeadsCharts stats={stats} chartsReady={chartsReady} />
    </>
  );
};

export const FlowLeadsListTab = ({
  rows,
  stats,
  loading,
  searchText,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  page,
  perPage,
  total,
  onPaginationChange,
  onCopy,
  emptyDescription = 'No flow submissions yet',
  cardsPerRow = 3,
  pageSizeOptions = ['6', '12', '18', '24'],
  onExport,
  exportLoading,
  onCreateGroup,
}) => {
  const colProps = cardsPerRow === 4
    ? { xs: 24, sm: 12, lg: 6 }
    : { xs: 24, md: 8 };

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search phone or answers…"
          value={searchText}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ flex: 1, minWidth: 220 }}
        />
        {stats?.is_dynamic && (
          <Segmented
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Completed', value: 'completed' },
              { label: 'In progress', value: 'in_progress' },
            ]}
          />
        )}
        {onExport && (
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={onExport}
            loading={exportLoading}
            style={{ background: '#27ae60', borderColor: '#27ae60' }}
          >
            Export Excel
          </Button>
        )}
        {onCreateGroup && (
          <Button
            icon={<TeamOutlined />}
            onClick={onCreateGroup}
          >
            Export To Group
          </Button>
        )}
      </div>

      {rows.length === 0 ? (
        !loading && (
          <Empty description={emptyDescription} style={{ marginTop: 60 }} />
        )
      ) : (
        <>
          <Row gutter={[12, 12]}>
            {rows.map((lead) => (
              <Col {...colProps} key={lead.id}>
                <LeadCard lead={lead} onCopy={onCopy} />
              </Col>
            ))}
          </Row>

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <Pagination
              current={page}
              pageSize={perPage}
              total={total}
              showSizeChanger
              pageSizeOptions={pageSizeOptions}
              showTotal={(t) => `${t} lead${t === 1 ? '' : 's'}`}
              onChange={onPaginationChange}
            />
          </div>
        </>
      )}
    </>
  );
};
